/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { set, cloneDeep } from 'lodash';
import { connect } from 'react-redux';
import React, { useEffect } from 'react';
import { bindActionCreators } from 'redux';

import { TranslationRulesClient } from '@upstart/harmony/lib/legos/translationRulesTypes';
import SidebarContainer from '../SidebarContainer';
import dealersActions from '../../redux/dealers/actions';
import feedsActions from '../../redux/feeds/actions';
import verificationsActions from '../../redux/verifications/actions';
import groupActions from '../../redux/groups/actions';
import websiteActions from '../../redux/websites/actions';
import feedviewsActions from '../../redux/feedviews/actions';
import ErrorMessage from '../../components/ErrorMessage';
import './Dealer.scss';
import LoadingOverlay from '../../components/LoadingOverlay';

import { DealerEditor, NewDealer } from '../../components/DealerEditor';
import ContentPage from '../ContentPage';
import DealerNav from '../../components/DealerNav';
import * as translationsActions from '../../redux/translations/actions';
import * as translationRulesActions from '../../redux/translationRules/actions';

export const DAY_LIST = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

export const PICKUP_DEFAULTS = {
  appointmentWindow: '30',
  startsAfter: '12 hours',
  endsAfter: '5 days',
  days: {
    ...DAY_LIST.reduce(
      (prev, day) => ({
        ...prev,
        [day]: { enabled: !day.startsWith('s'), start: 10, end: 19 },
      }),
      {},
    ),
  },
};

export const DELIVERY_DEFAULTS = {
  enabled: false,
  startsAfter: '12 hours',
  endsAfter: '5 days',
  radius: 50,
  days: {
    ...DAY_LIST.reduce(
      (prev, day) => ({
        ...prev,
        [day]: { enabled: !day.startsWith('s'), start: 10, end: 19 },
      }),
      {},
    ),
  },
};

function Dealer(props) {
  const {
    dealers,
    actions,
    history,
    feeds,
    groups,
    feedviews,
    translations,
    translationRules,
    fetching,
  } = props;
  const { dealer, error } = dealers;
  const dealerFeesEnglish =
    translations?.instore?.['en-US']?.global?.dealerFees;
  const dealerFeesSpanish =
    translations?.instore?.['es-MX']?.global?.dealerFees;
  if (dealerFeesEnglish || dealerFeesSpanish) {
    dealer.translationsToBeSaved = { dealerFeesEnglish, dealerFeesSpanish };
  }
  const { dealerId, step } = props.match.params;
  const {
    feedsRequest,
    groupsRequest,
    createDealerRequest,
    updateDealerRequest,
    feedviewsRequest,
    translationRequest,
    translationUpdateRequest,
    translationRulesRequest,
  } = actions;

  useEffect(() => {
    if (!error) {
      feedsRequest();
      groupsRequest();
      feedviewsRequest();
      translationRequest({ dealerId });
      translationRulesRequest({ client: TranslationRulesClient.INSTORE });
    }
  }, [error, feedsRequest, feedviewsRequest, groupsRequest]);

  const onCreate = params => {
    // Default pickup and delivery are needed to display properly
    createDealerRequest({
      ...params,
      wizard: {
        pickup: PICKUP_DEFAULTS,
        delivery: DELIVERY_DEFAULTS,
        enableProdigyWeb: { enabled: true },
      },
    });
  };

  const onSave = payload => {
    if (payload.dealer?.translationsToBeSaved) {
      const changedTranslations = cloneDeep(translations);
      const {
        dealerFeesEnglish: newDealerFeesEnglish,
        dealerFeesSpanish: newDealerFeesSpanish,
      } = payload.dealer.translationsToBeSaved;
      // Basically means a Spanish/English translation cannot be made empty when it has a value. Can be "removed" by just putting an empty space
      if (newDealerFeesEnglish) {
        set(
          changedTranslations,
          'instore.en-US.global.dealerFees',
          newDealerFeesEnglish,
        );
        set(
          changedTranslations,
          'online.en-US.global.dealerFees',
          newDealerFeesEnglish,
        );
      }
      if (newDealerFeesSpanish) {
        set(
          changedTranslations,
          'instore.es-MX.global.dealerFees',
          newDealerFeesSpanish,
        );
        set(
          changedTranslations,
          'online.es-MX.global.dealerFees',
          newDealerFeesSpanish,
        );
      }
      translationUpdateRequest({ dealerId, translations: changedTranslations });
    }
    updateDealerRequest({
      ...payload,
      route: `/dealer/${dealerId}`,
    });
  };

  const setStep = s => {
    props.history.push(`/dealer/${dealerId}/${s}`);
  };

  return (
    <SidebarContainer
      title={dealer.name ? dealer.name : 'Create a Dealer'}
      openSubMenu="Client Management"
      logo={dealer.logo}
    >
      <div className="dealers-table">
        {dealer.id && <DealerNav />}
        {error && <ErrorMessage error={error} />}
        {dealer.id && (
          <LoadingOverlay active={fetching}>
            <DealerEditor
              feeds={feeds}
              dealer={{ ...dealer, countryToggle: dealer.country }}
              groups={groups}
              fetching={fetching}
              step={parseInt(step, 10)}
              onSave={onSave}
              setStep={setStep}
              actions={actions}
              feedviews={feedviews}
              onInventoryImport={actions.inventoryImportRequest}
              translations={translations}
              translationRules={translationRules}
            />
          </LoadingOverlay>
        )}
        {!dealer.id && (
          <NewDealer
            dealer={dealer}
            fetching={fetching}
            history={history}
            onCreate={onCreate}
          />
        )}
      </div>
    </SidebarContainer>
  );
}

Dealer.propTypes = {
  actions: PropTypes.object,
  dealers: PropTypes.object,
  feeds: PropTypes.array,
  groups: PropTypes.array,
  history: PropTypes.object,
  match: PropTypes.object,
  feedviews: PropTypes.object,
  translations: PropTypes.object,
  fetching: PropTypes.bool,
};

const mapStateToProps = ({
  dealers,
  feeds,
  groups,
  feedviews,
  translations,
  translationRules,
}) => ({
  dealers,
  feeds: feeds.feeds,
  groups: groups?.groups,
  feedviews: feedviews?.feedviews,
  translations: translations.data,
  translationRules: translationRules.data,
  fetching:
    dealers.fetching || feeds.fetching || groups.fetching || feedviews.fetching,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      ...dealersActions,
      ...feedsActions,
      ...verificationsActions,
      ...groupActions,
      ...feedviewsActions,
      ...translationsActions,
      ...translationRulesActions,
      ...websiteActions,
    },
    dispatch,
  ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ContentPage(Dealer));
